import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  ChevronDown,
  User,
  Bell
} from "lucide-react";

// Request browser notification permission
const requestNotificationPermission = async () => {
  if ("Notification" in window && Notification.permission === "default") {
    await Notification.requestPermission();
  }
};

// Show browser notification
const showBrowserNotification = (title, body, onClick) => {
  if ("Notification" in window && Notification.permission === "granted") {
    const notification = new Notification(title, {
      body,
      icon: "/favicon.ico",
      badge: "/favicon.ico",
      tag: "temple-of-sound-message",
      renotify: true
    });
    
    notification.onclick = () => {
      window.focus();
      if (onClick) onClick();
      notification.close();
    };
  }
};

const ChatWidget = () => {
  const { user, token, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const lastMessageIdRef = useRef(null);
  const previousUnreadRef = useRef(0);

  // Check if widget should be shown
  const shouldShow = isAuthenticated && user && !user?.is_manager && user?.role !== "Manager" && user?.role !== "Team Member";

  // Request notification permission on mount
  useEffect(() => {
    if (shouldShow) {
      requestNotificationPermission().then(() => {
        setNotificationsEnabled(Notification.permission === "granted");
      });
    }
  }, [shouldShow]);

  // Handle new message notifications
  const handleNewMessageNotification = useCallback((senderName, content) => {
    // Show toast notification
    toast.message(`New message from ${senderName}`, {
      description: content.length > 50 ? content.substring(0, 50) + "..." : content,
      action: {
        label: "View",
        onClick: () => setIsOpen(true)
      },
      duration: 5000
    });

    // Show browser notification if tab not focused
    if (document.hidden && notificationsEnabled) {
      showBrowserNotification(
        `Message from ${senderName}`,
        content.length > 100 ? content.substring(0, 100) + "..." : content,
        () => setIsOpen(true)
      );
    }

    // Play notification sound
    try {
      const audio = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleAUPPIjF8NiDAAA=");
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {
      // Audio playback failed - silently ignore
    }
  }, [notificationsEnabled]);

  useEffect(() => {
    if (!shouldShow) return;
    
    if (isOpen) {
      fetchConversations();
      fetchUnreadCount();
      // Poll for new messages every 5 seconds when open
      pollIntervalRef.current = setInterval(() => {
        fetchUnreadCount();
        if (activeConversation) {
          fetchMessages(activeConversation);
        }
      }, 5000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen, activeConversation]);

  useEffect(() => {
    if (!shouldShow) return;
    
    // Poll for unread count even when closed - check more frequently for notifications
    const countInterval = setInterval(async () => {
      await fetchUnreadCount();
      
      // Check for new messages to notify
      if (!isOpen) {
        await checkForNewMessages();
      }
    }, 10000);
    
    fetchUnreadCount();
    return () => clearInterval(countInterval);
  }, [shouldShow, isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check for new messages and show notifications
  const checkForNewMessages = async () => {
    try {
      const res = await axios.get(`${API}/messaging/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const convs = res.data || [];
      for (const conv of convs) {
        if (conv.last_message && conv.last_message.sender_id !== user?.id) {
          const msgId = conv.last_message.id || conv.last_message.created_at;
          if (lastMessageIdRef.current !== msgId && conv.unread_count > 0) {
            lastMessageIdRef.current = msgId;
            handleNewMessageNotification(
              conv.last_message.sender_name || "Support",
              conv.last_message.content
            );
            break;
          }
        }
      }
    } catch (err) {
      console.error("Failed to check for new messages:", err);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/messaging/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newCount = res.data.unread_count || 0;
      
      // If unread count increased while closed, we have new messages
      if (!isOpen && newCount > previousUnreadRef.current && previousUnreadRef.current >= 0) {
        // New message notification will be handled by checkForNewMessages
      }
      
      previousUnreadRef.current = newCount;
      setUnreadCount(newCount);
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
    }
  };

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/messaging/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(res.data || []);
    } catch (err) {
      console.error("Failed to fetch conversations:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const res = await axios.get(`${API}/messaging/conversation/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    }
  };

  const startNewConversation = async () => {
    // For B2B users, find a manager to chat with
    // In a real app, you might show a list of available managers
    // For now, we'll create a placeholder that gets a manager on first message
    setActiveConversation("new");
    setMessages([]);
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    setSendingMessage(true);
    try {
      // If this is a new conversation, we need to find a manager to send to
      // For simplicity, we'll use the first message to find/create the conversation
      const endpoint = activeConversation === "new" 
        ? `${API}/messaging/send-to-support`
        : `${API}/messaging/send`;
      
      const payload = activeConversation === "new"
        ? { content: newMessage.trim() }
        : { 
            recipient_id: conversations.find(c => c.id === activeConversation)?.other_user?.id,
            content: newMessage.trim() 
          };

      const res = await axios.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.conversation_id) {
        setActiveConversation(res.data.conversation_id);
      }
      
      setNewMessage("");
      fetchMessages(res.data.conversation_id || activeConversation);
      fetchConversations();
    } catch (err) {
      console.error("Failed to send message:", err);
    } finally {
      setSendingMessage(false);
    }
  };

  const selectConversation = (conv) => {
    setActiveConversation(conv.id);
    fetchMessages(conv.id);
  };

  // Don't render if user shouldn't see the widget
  if (!shouldShow) {
    return null;
  }

  return (
    <>
      {/* Floating Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#D4AF37] hover:bg-[#B8860B] text-black rounded-full shadow-lg flex items-center justify-center transition-colors"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        data-testid="chat-widget-button"
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
        {!isOpen && unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </motion.button>

      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-50 w-80 sm:w-96 h-[500px] bg-[#0C0C0C] border border-zinc-800 rounded-xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#D4AF37] to-[#B8860B] px-4 py-3">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-black">Messages</h3>
                {activeConversation && activeConversation !== "new" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setActiveConversation(null)}
                    className="text-black hover:bg-black/10"
                  >
                    <ChevronDown className="h-4 w-4 mr-1" />
                    Back
                  </Button>
                )}
              </div>
            </div>

            {/* Content */}
            {!activeConversation ? (
              // Conversation List
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center items-center h-full">
                    <Loader2 className="h-6 w-6 text-[#D4AF37] animate-spin" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center p-4">
                    <MessageCircle className="h-12 w-12 text-zinc-700 mb-3" />
                    <p className="text-zinc-400 mb-4">No conversations yet</p>
                    <Button
                      onClick={startNewConversation}
                      className="bg-[#D4AF37] hover:bg-[#B8860B] text-black"
                    >
                      Start a Conversation
                    </Button>
                  </div>
                ) : (
                  <div className="divide-y divide-zinc-800">
                    {conversations.map(conv => (
                      <button
                        key={conv.id}
                        onClick={() => selectConversation(conv)}
                        className="w-full p-4 text-left hover:bg-zinc-900 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
                            <User className="h-5 w-5 text-zinc-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-white truncate">
                                {conv.other_user?.name || "Support"}
                              </p>
                              {conv.unread_count > 0 && (
                                <Badge className="bg-[#D4AF37] text-black text-xs">
                                  {conv.unread_count}
                                </Badge>
                              )}
                            </div>
                            {conv.last_message && (
                              <p className="text-sm text-zinc-500 truncate">
                                {conv.last_message.content}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                    <div className="p-4">
                      <Button
                        onClick={startNewConversation}
                        variant="outline"
                        className="w-full border-zinc-700 text-zinc-300"
                      >
                        New Conversation
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Messages View
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">
                      <p>Send a message to start the conversation</p>
                    </div>
                  ) : (
                    messages.map(msg => {
                      const isMe = msg.sender_id === user?.id;
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isMe ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg px-3 py-2 ${
                              isMe
                                ? "bg-[#D4AF37] text-black"
                                : "bg-zinc-800 text-white"
                            }`}
                          >
                            <p className="text-sm">{msg.content}</p>
                            <p className={`text-xs mt-1 ${isMe ? "text-black/60" : "text-zinc-500"}`}>
                              {new Date(msg.created_at).toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit"
                              })}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="border-t border-zinc-800 p-3">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 bg-zinc-900 border-zinc-700 text-sm"
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
                    />
                    <Button
                      onClick={sendMessage}
                      disabled={sendingMessage || !newMessage.trim()}
                      size="sm"
                      className="bg-[#D4AF37] hover:bg-[#B8860B] text-black"
                    >
                      {sendingMessage ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
